import {
  Drawer,
  Form,
  Input,
  Select,
  Switch,
  Button,
  Space,
  Skeleton,
  Descriptions,
  Typography,
} from "antd";
import { FormattedMessage, useIntl } from "react-intl";
import { CustomerVoipConfig } from "@lib/types/Voip";
import { useCallback, useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { apiGetCustomerVoipConfig } from "@lib/api/voip";
import { apiGetCustomers } from "@lib/api/customer";
import CustomerName from "@lib/components/MiniComponents/Others/CustomerName";
import { Tag } from "antd";
import dayjs from "dayjs";
import { StyledFlex } from "@lib/components/MiniComponents/Table/index.styled";
import styled from "styled-components";
import { UseMutationProps } from "@lib/types/Api";

const StyledDescriptions = styled(Descriptions)`
  th,
  td {
    padding: 5px !important;
  }
`;

const StyledDrawer = styled(Drawer)`
  .ant-descriptions-item-label {
    width: 200px;
  }
`;

const layout = {
  labelCol: { span: 8 },
  wrapperCol: { span: 16 },
};

type Props = {
  open: boolean;
  onClose: () => void;
  item: CustomerVoipConfig | null;
  createMutation: UseMutationProps<CustomerVoipConfig>;
  updateMutation: UseMutationProps<Partial<CustomerVoipConfig>>;
};

const SkeletonContent = () => (
  <StyledDescriptions bordered column={1}>
    {Array.from({ length: 9 }).map((_, index) => (
      <Descriptions.Item
        key={index}
        label={<Skeleton.Input active size="small" style={{ width: 100 }} />}
      >
        <Skeleton.Input active size="small" style={{ width: 200 }} />
      </Descriptions.Item>
    ))}
  </StyledDescriptions>
);

export const CustomerVoipConfigDrawer = (props: Props) => {
  const { open, onClose, item, createMutation, updateMutation } = props;
  const { messages, formatMessage } = useIntl();
  const [form] = Form.useForm();
  const [isEditMode, setIsEditMode] = useState(false);

  const configQuery = useQuery({
    queryFn: () => apiGetCustomerVoipConfig(item?.id || ""),
    queryKey: ["customer-voip-config", item?.id],
    enabled: !!item?.id && open && !isEditMode,
  });

  const configData = configQuery?.data?.data || item;
  const isLoading = configQuery?.isLoading || configQuery?.isFetching;

  const { data: customers, isLoading: customersIsLoading } = useQuery({
    queryFn: () => apiGetCustomers({ limit: 100 }),
    queryKey: ["customers-dropdown"],
    enabled: open && isEditMode,
  });

  useEffect(() => {
    if (open) {
      if (item) {
        setIsEditMode(false);
        form?.setFieldsValue(configData);
      } else {
        setIsEditMode(true);
        form?.resetFields();
        form?.setFieldsValue({
          isDisabled: false,
        });
      }
    }
  }, [open, item, configData]);

  const handleClose = useCallback(() => {
    setIsEditMode(false);
    form?.resetFields();
    onClose();
  }, [onClose, form]);

  const handleSubmit = useCallback(
    (values: Partial<CustomerVoipConfig>) => {
      if (item?.id) {
        updateMutation.mutate({
          id: item.id,
          ...values,
        });
        setIsEditMode(false);
        configQuery?.refetch();
      } else {
        createMutation.mutate(values as CustomerVoipConfig);
      }
    },
    [item, createMutation, updateMutation, configQuery],
  );

  const handleReset = useCallback(() => {
    if (item) {
      form?.setFieldsValue(configData);
    } else {
      form?.resetFields();
      form?.setFieldsValue({
        isDisabled: false,
      });
    }
  }, [form, item, configData]);

  const isPending = createMutation.isPending || updateMutation.isPending;

  return (
    <StyledDrawer
      title={
        <FormattedMessage
          id={
            isEditMode
              ? item
                ? "common.editTitle"
                : "common.createTitle"
              : "common.previewTitle"
          }
          values={{
            title: messages["sidebar.customerVoipConfigs"] as string,
          }}
        />
      }
      onClose={handleClose}
      open={open}
      width={600}
      id="customer-voip-config-drawer"
      extra={
        !isEditMode &&
        item && (
          <Button
            type="primary"
            onClick={() => setIsEditMode(true)}
            disabled={isLoading}
          >
            <FormattedMessage id="common.edit" />
          </Button>
        )
      }
    >
      {isEditMode ? (
        <Form<CustomerVoipConfig>
          {...layout}
          form={form}
          name="customer-voip-config-form"
          onFinish={handleSubmit}
          style={{ maxWidth: 600 }}
          autoComplete="off"
        >
          <Form.Item
            name="customerId"
            label={messages["common.customer"] as string}
            rules={[
              {
                required: true,
                message: formatMessage(
                  { id: "validation.required" },
                  { field: messages["common.customer"] as string },
                ),
              },
            ]}
          >
            <Select
              placeholder={formatMessage(
                {
                  id: "helper.pleaseSelectToX",
                },
                { x: messages["common.customer"] as string },
              )}
              allowClear
              showSearch
              loading={customersIsLoading}
              optionFilterProp="text"
              disabled={!!item?.id}
              options={customers?.data?.data?.map((customer) => ({
                label: <CustomerName customer={customer} />,
                text: customer.name,
                value: customer.id,
              }))}
            />
          </Form.Item>

          <Form.Item
            name="name"
            label={messages["common.name"] as string}
            rules={[
              {
                required: true,
                message: formatMessage(
                  { id: "validation.required" },
                  { field: messages["common.name"] as string },
                ),
              },
            ]}
          >
            <Input
              placeholder={formatMessage(
                {
                  id: "helper.pleaseTypeX",
                },
                { x: messages["common.name"] as string },
              )}
            />
          </Form.Item>

          <Form.Item
            name="phone"
            label={messages["common.phone"] as string}
            rules={[
              {
                required: true,
                message: formatMessage(
                  { id: "validation.required" },
                  { field: messages["common.phone"] as string },
                ),
              },
            ]}
          >
            <Input
              placeholder={formatMessage(
                {
                  id: "helper.pleaseTypeX",
                },
                { x: messages["common.phone"] as string },
              )}
            />
          </Form.Item>

          <Form.Item
            name="terminationURI"
            label={messages["common.terminationURI"] as string}
            rules={[
              {
                required: true,
                message: formatMessage(
                  { id: "validation.required" },
                  { field: messages["common.terminationURI"] as string },
                ),
              },
            ]}
          >
            <Input
              placeholder={formatMessage(
                {
                  id: "helper.pleaseTypeX",
                },
                { x: messages["common.terminationURI"] as string },
              )}
            />
          </Form.Item>

          <Form.Item
            name="username"
            label={messages["common.username"] as string}
          >
            <Input
              placeholder={formatMessage(
                {
                  id: "helper.pleaseTypeX",
                },
                { x: messages["common.username"] as string },
              )}
            />
          </Form.Item>

          <Form.Item
            name="password"
            label={messages["common.password"] as string}
          >
            <Input.Password
              placeholder={formatMessage(
                {
                  id: "helper.pleaseTypeX",
                },
                { x: messages["common.password"] as string },
              )}
            />
          </Form.Item>

          <Form.Item
            name="nickname"
            label={messages["common.nickname"] as string}
          >
            <Input
              placeholder={formatMessage(
                {
                  id: "helper.pleaseTypeX",
                },
                { x: messages["common.nickname"] as string },
              )}
            />
          </Form.Item>

          <Form.Item
            name="isDisabled"
            label={messages["common.isDisabled"] as string}
            valuePropName="checked"
          >
            <Switch
              checkedChildren={messages["common.enabled"] as string}
              unCheckedChildren={messages["common.disabled"] as string}
            />
          </Form.Item>

          <StyledFlex style={{ justifyContent: "end" }}>
            <Space>
              <Button
                htmlType="button"
                onClick={() => {
                  if (item) {
                    setIsEditMode(false);
                    handleReset();
                  } else {
                    handleClose();
                  }
                }}
                disabled={isPending}
              >
                {messages["common.cancel"] as string}
              </Button>
              <Button
                htmlType="button"
                onClick={handleReset}
                disabled={isPending}
              >
                {messages["common.reset"] as string}
              </Button>
              <Button type="primary" htmlType="submit" loading={isPending}>
                {item?.id
                  ? (messages["common.update"] as string)
                  : (messages["common.create"] as string)}
              </Button>
            </Space>
          </StyledFlex>
        </Form>
      ) : isLoading ? (
        <SkeletonContent />
      ) : (
        <StyledDescriptions bordered column={1}>
          <Descriptions.Item label="ID">
            <Typography.Text copyable={{ text: configData?.id }}>
              {configData?.id || "-"}
            </Typography.Text>
          </Descriptions.Item>

          <Descriptions.Item label={<FormattedMessage id="common.customer" />}>
            <CustomerName customer={configData?.customer} link />
          </Descriptions.Item>

          <Descriptions.Item label={<FormattedMessage id="common.name" />}>
            {configData?.name || "-"}
          </Descriptions.Item>

          <Descriptions.Item label={<FormattedMessage id="common.phone" />}>
            {configData?.phone || "-"}
          </Descriptions.Item>

          <Descriptions.Item
            label={<FormattedMessage id="common.terminationURI" />}
          >
            {configData?.terminationURI || "-"}
          </Descriptions.Item>

          <Descriptions.Item label={<FormattedMessage id="common.username" />}>
            {configData?.username || "-"}
          </Descriptions.Item>

          <Descriptions.Item label={<FormattedMessage id="common.nickname" />}>
            {configData?.nickname || "-"}
          </Descriptions.Item>

          <Descriptions.Item
            label={<FormattedMessage id="common.isDisabled" />}
          >
            <Tag color={configData?.isDisabled ? "red" : "green"}>
              {configData?.isDisabled
                ? (messages["common.disabled"] as string)
                : (messages["common.enabled"] as string)}
            </Tag>
          </Descriptions.Item>

          <Descriptions.Item label={<FormattedMessage id="common.createdAt" />}>
            {configData?.createdAt
              ? dayjs(configData.createdAt).format("DD/MM/YYYY HH:mm:ss")
              : "-"}
          </Descriptions.Item>

          <Descriptions.Item label={<FormattedMessage id="common.updatedAt" />}>
            {configData?.updatedAt
              ? dayjs(configData.updatedAt).format("DD/MM/YYYY HH:mm:ss")
              : "-"}
          </Descriptions.Item>
        </StyledDescriptions>
      )}
    </StyledDrawer>
  );
};
